import { DashboardRoutes } from "@/router/constant";
import { formatCardNumber } from "@/utils/formatCardNumber";
import { formatIBAN } from "@/utils/formatIBAN";
import { useAppTranslation } from "@/utils/useAppTranslation";
import type { DepositIdentifierDetails } from "@payping/api";
import { CopyOutlined, InfoCircleFilled } from "@payping/icons";
import { getBankInfoFromCardNumber, getBankInfoFromSheba } from "@payping/utilities-shared/bank";
import { dateFormats, toJalali } from "@payping/utils/dateUtils";
import { Bank, Button, Col, Descriptions, Modal, Row, Space, Typography, message } from "@payping/web-components";
import copy from "copy-to-clipboard";
import { Dispatch, FC } from "react";
import { useNavigate } from "react-router-dom";

interface Props {
  setData: Dispatch<DepositIdentifierDetails | undefined>;
  data?: DepositIdentifierDetails;
}

const Success: FC<Props> = (props) => {
  const { data, setData } = props;
  const { t } = useAppTranslation();
  const navigate = useNavigate();

  return (
    <Modal
      mask={{ closable: true }}
      title={t("settlement.id.idShow")}
      open={Boolean(data)}
      width={700}
      onCancel={() => {
        setData(undefined);
        navigate(DashboardRoutes.settlement.id.url);
      }}
      footer={null}
    >
      <Row justify={"center"} gutter={[16, 16]}>
        <Col xs={24} className="tw:flex tw:justify-center">
          <InfoCircleFilled className="color--info" width={70} />
        </Col>
        <Col xs={24} className="tw:flex tw:justify-center">
          <Typography.Title level={4}>{t("settlement.id.get")}</Typography.Title>
        </Col>
        <Col xs={24}>
          <div className="background--gray100 tw:px-8! tw:py-4!">
            <Space orientation={"vertical"} size={"middle"}>
              <Space orientation={"vertical"} size={2}>
                <Typography.Text>{t("settlement.id.serial")}</Typography.Text>
                <Button
                  type={"dashed"}
                  icon={<CopyOutlined width={14} height={14} />}
                  onClick={() => {
                    if (data?.depositId) {
                      const copied = copy(data?.depositId);
                      if (copied) message.success({ content: t("messages.copied") });
                    }
                  }}
                >
                  {data?.depositId}
                </Button>
              </Space>
              <Typography.Text>{t("settlement.id.details")}</Typography.Text>
              <Descriptions className="tw:w-full" column={2}>
                <Descriptions.Item label={t("settlement.id.payerType")}>
                  <Typography.Text type={"secondary"}>{data?.isLegal ? t("legal") : t("natural")}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={data?.isLegal ? t("settings.business.registrationDate") : t("birthDate")}>
                  <Typography.Text type={"secondary"}>
                    {
                      toJalali({
                        date: data?.isLegal ? data?.registerDate : data?.birthDate,
                        toFormat: dateFormats.ui.jalaliDate,
                      }).string
                    }
                  </Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={data?.isLegal ? t("name") : t("fullName")}>
                  <Typography.Text type={"secondary"}>
                    {data?.isLegal ? data?.title : `${data?.firstName} ${data?.lastName}`}
                  </Typography.Text>
                </Descriptions.Item>
                {!data?.isLegal ? (
                  <Descriptions.Item label={t("mobileNumber")}>
                    <Typography.Text type={"secondary"}>{data?.phoneNumber}</Typography.Text>
                  </Descriptions.Item>
                ) : null}
                {!data?.isLegal ? (
                  <Descriptions.Item label={t("nationalCode")}>
                    <Typography.Text type={"secondary"}>{data?.nationalCode}</Typography.Text>
                  </Descriptions.Item>
                ) : null}
                {data?.isLegal ? (
                  <Descriptions.Item label={t("bNationalCode")}>
                    <Typography.Text type={"secondary"}>{data?.businessNationalId}</Typography.Text>
                  </Descriptions.Item>
                ) : null}
                <Descriptions.Item label={t("description")}>
                  <Typography.Text type={"secondary"}>{data?.description || "-"}</Typography.Text>
                </Descriptions.Item>
                <Descriptions.Item label={t("settlement.id.iban")}>&nbsp;</Descriptions.Item>
                <Descriptions.Item label={t("settlement.id.cardNumber")}>&nbsp;</Descriptions.Item>
                <Descriptions.Item>
                  <Space orientation={"vertical"} className={"tw:w-full"} size={"middle"}>
                    {(data?.sourceIbans || []).map((item, index) => (
                      <Space key={index}>
                        <Typography.Text className={"en-font"} type={"secondary"} key={index}>
                          {formatIBAN(item)}
                        </Typography.Text>
                        <Bank.Icon showTooltip bankName={getBankInfoFromSheba(item)?.name} />
                      </Space>
                    ))}
                  </Space>
                </Descriptions.Item>

                <Descriptions.Item>
                  <Space orientation={"vertical"} className={"tw:w-full"} size={"middle"}>
                    {(data?.sourceCardNumbers || []).map((item, index) => (
                      <Space key={index}>
                        <span className={"tw:align-start tw:flex"}>
                          <div className={"ltr"}>
                            <Typography.Text className={"en-font"} strong>
                              {formatCardNumber(item)}
                            </Typography.Text>
                          </div>
                        </span>
                        <Bank.Icon showTooltip bankName={getBankInfoFromCardNumber(item)?.name} />
                      </Space>
                    ))}
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Space>
          </div>
        </Col>
      </Row>
    </Modal>
  );
};

export default Success;
